import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";

type WSContext = {
  socket: WebSocket | null;
  isConnected: boolean;
};
const WebSocketContext = createContext<WSContext>({
  socket: null,
  isConnected: false,
});

interface WebSocketProviderProps {
  children: ReactNode;
}

function getWsBaseUrl() {
  const envUrl = process.env.REACT_APP_API_URL?.trim();
  if (envUrl) {
    const clean = envUrl.replace(/\/+$/, "");
    return clean.replace(/^http(s?):/, "ws$1:") + "/ws";
  }

  const { hostname, protocol } = window.location;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "ws://localhost:3001/ws";
  }

  const wsProto = protocol === "https:" ? "wss" : "ws";
  return `${wsProto}://${window.location.host}/ws`;
}

const MAX_RECONNECT_DELAY = 30_000;
const INITIAL_RECONNECT_DELAY = 1_000;
const PING_INTERVAL = 30_000;
// Una conexión debe SOBREVIVIR al menos esto para considerarse estable y
// resetear el backoff. Sin este umbral, una conexión que abre y muere en ~1 s
// (p.ej. el server la cierra apenas completa el handshake por cap de
// conexiones) reseteaba el backoff a 1 s en CADA `onopen` → reconexión cada
// segundo indefinida (y con ella un refetch del calendario cada segundo). Con
// el umbral, un ciclo abre-y-muere sigue escalando el backoff 1→2→4…→30 s.
// Round 2026-09-09.
const STABLE_CONNECTION_MS = 5_000;

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const { token } = useAuth();
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const reconnectDelay = useRef(INITIAL_RECONNECT_DELAY);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Timer que, si la conexión sobrevive `STABLE_CONNECTION_MS`, resetea el
  // backoff. Se cancela en `onclose`/`cleanup` si la conexión muere antes.
  const stableTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalClose = useRef(false);
  // Round 2026-05-11: anti-spam de logs cuando el WS no puede conectarse
  // y entra en loop de reconexión exponencial. Logueamos solo el PRIMER
  // error de cada racha (`alreadyLoggedError`) y reseteamos el flag al
  // próximo `onopen` exitoso. Sin esto, una pestaña abierta horas con
  // backend caído deja la consola con cientos de "WS error Event".
  const alreadyLoggedError = useRef(false);

  const cleanup = useCallback(() => {
    if (pingTimer.current) {
      clearInterval(pingTimer.current);
      pingTimer.current = null;
    }
    if (stableTimer.current) {
      clearTimeout(stableTimer.current);
      stableTimer.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!token) return;

    // Evitar conexiones duplicadas
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    intentionalClose.current = false;
    // SEGURIDAD (P2.2 de docs/seguridad.md): pasamos el JWT vía
    // Sec-WebSocket-Protocol ("bearer, <token>") en vez de query string.
    // La query string queda en logs de proxy (Railway, Cloudflare) y
    // en Referer headers; el subprotocolo NO. El backend también acepta
    // ?token= por compatibilidad con clientes viejos, pero avisa por WARN.
    const ws = new WebSocket(getWsBaseUrl(), ["bearer", token]);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setSocket(ws);
      // NO reseteamos el backoff de inmediato: un `onopen` no garantiza una
      // conexión SANA (el server puede cerrarla al instante por cap). Solo
      // reseteamos si la conexión sobrevive `STABLE_CONNECTION_MS`; si muere
      // antes, `onclose` cancela este timer y el backoff sigue escalando —
      // así un ciclo abre-y-muere NO queda pegado en 1 s.
      if (stableTimer.current) clearTimeout(stableTimer.current);
      stableTimer.current = setTimeout(() => {
        reconnectDelay.current = INITIAL_RECONNECT_DELAY;
        // Reset del flag de log: si la próxima racha falla, queremos ver
        // el primer error de esa racha nueva.
        alreadyLoggedError.current = false;
        stableTimer.current = null;
      }, STABLE_CONNECTION_MS);

      // Keepalive
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "PING" }));
        }
      }, PING_INTERVAL);
    };

    ws.onerror = (e) => {
      // Solo el primer error de cada racha de reconexión va a consola.
      // Los siguientes se silencian hasta que la conexión vuelva (onopen
      // resetea el flag). Evita el spam de "WS error Event" cuando el
      // backend está caído o el JWT está invalidado.
      if (!alreadyLoggedError.current) {
        console.error(
          "WS error (silenciando próximos errores hasta reconexión exitosa)",
          e,
        );
        alreadyLoggedError.current = true;
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      setSocket(null);
      if (pingTimer.current) {
        clearInterval(pingTimer.current);
        pingTimer.current = null;
      }
      // La conexión murió: si aún no se había estabilizado, cancelar el reset
      // del backoff para que siga escalando (evita el loop de 1 s).
      if (stableTimer.current) {
        clearTimeout(stableTimer.current);
        stableTimer.current = null;
      }

      // Reconectar solo si no fue cierre intencional y hay token
      if (!intentionalClose.current && token) {
        const delay = reconnectDelay.current;
        reconnectTimer.current = setTimeout(() => {
          reconnectDelay.current = Math.min(
            reconnectDelay.current * 2,
            MAX_RECONNECT_DELAY,
          );
          connect();
        }, delay);
      }
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      // Sin token: cerrar y limpiar
      intentionalClose.current = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      cleanup();
      setSocket(null);
      setIsConnected(false);
      return;
    }

    connect();

    return () => {
      intentionalClose.current = true;
      cleanup();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [token, connect, cleanup]);

  // Memoizar el value evita que cada render del provider dispare re-renders
  // en todos los consumidores (useWebSocket) — importante porque este
  // context se usa en prácticamente toda la app.
  const value = useMemo(() => ({ socket, isConnected }), [socket, isConnected]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
